import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Collapse, Form, Modal, Spinner, Table } from "react-bootstrap";
import { saveAs } from "file-saver";
import {
  getAladdinExportReadiness,
  getAssessmentScreener,
  getClassesByAssessmentId,
  getClassesBySchoolId,
  getClassesAssessmentResults,
  getEntityTags,
  getTagDefinitions,
  getTeachersBySchoolId,
  postAladdinExportAuditLog,
  requestAladdinExportNotify,
  sendAladdinResultsEmail
} from "../services/TeacherAPIClient";
import {
  ALADDIN_IMPORT_PDF_URL,
  ALADDIN_IMPORT_VIDEO_URL,
  ALADDIN_IMPORT_VIDEO_EMBED_URL,
  buildAladdinEmailPayload,
  buildAladdinZipFileName,
  createAladdinResultsZip,
  getCurrentAcademicYear
} from "../services/AladdinExportHelper";
import {
  assertSubmittedRequestResponse,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  hasPlaceholderTargetId,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import {
  formatClassDisplayName,
  getClassAcademicYear,
  getDistinctClassGradeLabel
} from "../utils/classDisplay";
import "./TeacherRoundThreeModal.css";

const EXPORT_ROLES = {
  SENCO: "SENCO",
  PRINCIPAL: "PRINCIPAL",
  SECRETARY: "SECRETARY",
  ALPACA_LEAD: "ALPACA LEAD"
};

const STANDARDISED_SCORE_TAG = "standardised_scores_enabled";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const getClassId = (classItem) => classItem?.classId || classItem?.classid || classItem?.id;
const getClassName = (classItem) => classItem?.name || classItem?.className || classItem?.classname || "";

const getAssessmentName = (assessment) => assessment?.name || assessment?.assessmentName || assessment?.title || "";
const getAssessmentId = (assessment) => assessment?.id || assessment?.assessmentid || assessment?.assessmentId;

const isAssessmentLike = (assessment) => assessment && getAssessmentId(assessment) && assessment.task !== true;

const hasExportRole = (teacher = {}) => {
  const roleValues = [
    teacher.role,
    teacher.roleName,
    teacher.roleLabel,
    ...(Array.isArray(teacher.roles) ? teacher.roles : []),
    ...(Array.isArray(teacher.additionalRoles) ? teacher.additionalRoles : [])
  ];

  return roleValues.some((roleValue) => {
    const role = String(roleValue || "").toUpperCase();

    return (
      role === EXPORT_ROLES.SENCO ||
      role.includes(EXPORT_ROLES.PRINCIPAL) ||
      role === EXPORT_ROLES.SECRETARY ||
      role === EXPORT_ROLES.ALPACA_LEAD
    );
  });
};

const getTeacherLabel = (teacher) => teacher?.name || teacher?.teacherName || teacher?.email || "Teacher";

const buildClassSummary = (classes, selectedClassIds) =>
  selectedClassIds
    .map((classId) => {
      const classItem = classes.find((item) => getClassId(item) === classId);
      return classItem ? getClassName(classItem) || classId : classId;
    })
    .filter(Boolean);

const getStatusReady = (response) =>
  response?.status === "ready" ||
  response?.ready === true ||
  response?.data?.status === "ready" ||
  response?.data?.ready === true;

function TeacherAladdinExportModal({
  show,
  handleClose,
  prefill = null,
  teacherid = null,
  schoolid = null,
  countryId = "",
  countryGrades = null,
  teacherName = "",
  teacherEmail = "",
  embedded = false,
  onWorkflowComplete = null
}) {
  const academicYear = useMemo(() => getCurrentAcademicYear(), []);
  const [stage, setStage] = useState("screeners");
  const [screeners, setScreeners] = useState([]);
  const [selectedScreenerId, setSelectedScreenerId] = useState("");
  const [classes, setClasses] = useState([]);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherIds, setSelectedTeacherIds] = useState([]);
  const [isLoadingScreeners, setIsLoadingScreeners] = useState(false);
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);
  const [isLoadingTeachers, setIsLoadingTeachers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [readinessResults, setReadinessResults] = useState([]);
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);

  const selectedScreener = screeners.find((screener) => getAssessmentId(screener) === selectedScreenerId);
  const selectedAssessmentName = getAssessmentName(selectedScreener) || prefill?.assessmentName || prefill?.screenerName || "Assessment";
  const selectedClassNames = buildClassSummary(classes, selectedClassIds);
  const selectedClassLabels = selectedClassIds.map((classId) => {
    const classItem = classes.find((item) => getClassId(item) === classId);
    return classItem
      ? formatClassDisplayName(classItem, getClassName(classItem) || classId, countryGrades)
      : classId;
  });
  const exportTeachers = teachers.filter(hasExportRole);

  const loadTagEnabledScreeners = async () => {
    if (!schoolid) {
      setError("School context is missing. Refresh and try again.");
      return;
    }

    setIsLoadingScreeners(true);
    setError("");

    try {
      const [tagDefinitions, assessments] = await Promise.all([
        getTagDefinitions(),
        getAssessmentScreener({ countryid: countryId, active: true })
      ]);
      const standardisedScoreDef = (Array.isArray(tagDefinitions) ? tagDefinitions : []).find(
        (definition) => definition.tag_name === STANDARDISED_SCORE_TAG
      );

      if (!standardisedScoreDef) {
        throw new Error("Standardised score tag definition was not found.");
      }

      const assessmentRows = (Array.isArray(assessments) ? assessments : []).filter(isAssessmentLike);
      const taggedAssessments = await Promise.all(
        assessmentRows.map(async (assessment) => {
          const tags = await getEntityTags("assessment", getAssessmentId(assessment));
          const standardisedScoreTag = (Array.isArray(tags) ? tags : []).find(
            (tag) =>
              tag.tag_definition_id === standardisedScoreDef.id ||
              tag.tag_definition?.tag_name === STANDARDISED_SCORE_TAG
          );

          return standardisedScoreTag?.tag_value === "ON" ? assessment : null;
        })
      );

      const enabledScreeners = taggedAssessments.filter(Boolean);
      setScreeners(enabledScreeners);

      const prefillAssessmentId = prefill?.assessmentId || prefill?.assessment_id || "";
      const prefillScreenerName =
        prefill?.screenerName ||
        prefill?.screener_name ||
        prefill?.assessmentName ||
        prefill?.assessment_name ||
        "";
      const preselectedScreener =
        enabledScreeners.find((screener) => getAssessmentId(screener) === prefillAssessmentId) ||
        enabledScreeners.find((screener) => normaliseText(getAssessmentName(screener)).includes(normaliseText(prefillScreenerName)));

      if (preselectedScreener) {
        setSelectedScreenerId(getAssessmentId(preselectedScreener));
        setStage("classes");
      }
    } catch (loadError) {
      console.error("Error loading Aladdin export screeners:", loadError);
      setScreeners([]);
      setError("Screeners could not be loaded. Try again.");
    } finally {
      setIsLoadingScreeners(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    setStage("screeners");
    setScreeners([]);
    setSelectedScreenerId("");
    setClasses([]);
    setSelectedClassIds([]);
    setTeachers([]);
    setSelectedTeacherIds([]);
    setIsLoadingScreeners(false);
    setIsLoadingClasses(false);
    setIsLoadingTeachers(false);
    setIsSubmitting(false);
    setError("");
    setReadinessResults([]);
    setSupportReference("");
    setIsDuplicatePending(false);
    setShowGuidance(false);
    setSuccessDetails(null);
    loadTagEnabledScreeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, schoolid, countryId]);

  useEffect(() => {
    if (!show || !selectedScreenerId || !schoolid) return;

    let cancelled = false;

    async function loadClasses() {
      setIsLoadingClasses(true);
      setError("");

      try {
        const [filteredClasses, schoolClasses] = await Promise.all([
          getClassesByAssessmentId(selectedScreenerId, schoolid, {
            start: academicYear.start,
            end: academicYear.end
          }),
          getClassesBySchoolId(schoolid)
        ]);

        if (cancelled) return;

        const schoolClassMap = new Map(
          (Array.isArray(schoolClasses) ? schoolClasses : []).map((classItem) => [
            getClassId(classItem),
            classItem
          ])
        );
        const rows = (Array.isArray(filteredClasses) ? filteredClasses : []).map((classItem) => {
          const schoolClass = schoolClassMap.get(getClassId(classItem)) || {};

          return {
            ...schoolClass,
            ...classItem,
            studentNumber:
              classItem?.studentNumber ||
              classItem?.studentCount ||
              schoolClass?.studentNumber ||
              schoolClass?.studentCount ||
              0
          };
        });
        const prefillClassId = prefill?.classId || prefill?.class_id || "";
        const prefillClassName = prefill?.className || prefill?.class_name || "";
        const preselected = rows.filter(
          (classItem) =>
            getClassId(classItem) === prefillClassId ||
            normaliseText(getClassName(classItem)) === normaliseText(prefillClassName)
        );

        setClasses(rows);
        setSelectedClassIds(preselected.map(getClassId).filter(Boolean));
      } catch (loadError) {
        console.error("Error loading Aladdin export classes:", loadError);
        if (!cancelled) {
          setClasses([]);
          setSelectedClassIds([]);
          setError("Classes could not be loaded for this screener. Try again.");
        }
      } finally {
        if (!cancelled) setIsLoadingClasses(false);
      }
    }

    loadClasses();

    return () => {
      cancelled = true;
    };
  }, [academicYear.end, academicYear.start, prefill, schoolid, selectedScreenerId, show]);

  const toggleClassSelection = (classId) => {
    setSelectedClassIds((currentIds) =>
      currentIds.includes(classId)
        ? currentIds.filter((currentId) => currentId !== classId)
        : [...currentIds, classId]
    );
  };

  const handleSelectAllClasses = () => {
    if (selectedClassIds.length === classes.length) {
      setSelectedClassIds([]);
      return;
    }

    setSelectedClassIds(classes.map(getClassId).filter(Boolean));
  };

  const checkReadiness = async () => {
    if (!selectedScreenerId) {
      setError("Choose a screener before checking export readiness.");
      return;
    }

    if (selectedClassIds.length < 1) {
      setError("Choose at least one class before checking export readiness.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const results = await Promise.all(
        selectedClassIds.map(async (classId) => {
          const response = await getAladdinExportReadiness(schoolid, classId, selectedScreenerId);
          return {
            classId,
            response,
            ready: getStatusReady(response)
          };
        })
      );

      setReadinessResults(results);
      setStage(results.every((result) => result.ready) ? "ready" : "not-ready");
    } catch (readinessError) {
      console.error("Error checking Aladdin export readiness:", readinessError);
      setError("Export readiness could not be checked. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchAndPackageResults = async () => {
    const rawResults = await getClassesAssessmentResults(selectedClassIds, selectedScreenerId);

    if (!rawResults || Object.keys(rawResults).length < 1) {
      throw new Error("No results were found for the selected classes.");
    }

    return createAladdinResultsZip({
      rawResults,
      selectedClassIds,
      assessmentName: selectedAssessmentName,
      rollNumber: prefill?.rollNumber || prefill?.rollnumber || ""
    });
  };

  const handleDownloadExport = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      const { zip, classNames } = await fetchAndPackageResults();
      await postAladdinExportAuditLog(
        teacherid,
        schoolid,
        selectedAssessmentName,
        selectedScreenerId,
        selectedClassIds,
        classNames.length ? classNames : selectedClassNames,
        "Export"
      );
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(
        blob,
        buildAladdinZipFileName({
          rollNumber: prefill?.rollNumber || prefill?.rollnumber || "rollNumber",
          schoolName: prefill?.schoolName || prefill?.school_name || "School",
          assessmentName: selectedAssessmentName,
          academicYearKey: academicYear.key
        })
      );
      setSuccessDetails({
        type: "export",
        title: "Export Downloaded",
        message: `The ${selectedAssessmentName} export has been downloaded for ${selectedClassIds.length} class${selectedClassIds.length === 1 ? "" : "es"}.`
      });
      setStage("success");
    } catch (downloadError) {
      console.error("Error downloading Aladdin export:", downloadError);
      setError(downloadError?.message || "The export could not be downloaded. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const loadEmailRecipients = async () => {
    if (teachers.length > 0) return;

    setIsLoadingTeachers(true);
    setError("");

    try {
      const response = await getTeachersBySchoolId(schoolid);
      setTeachers(Array.isArray(response) ? response : []);
    } catch (loadError) {
      console.error("Error loading Aladdin export teachers:", loadError);
      setTeachers([]);
      setError("Teachers could not be loaded for email. Try again.");
    } finally {
      setIsLoadingTeachers(false);
    }
  };

  const openEmailStep = async () => {
    setStage("email");
    await loadEmailRecipients();
  };

  const handleSendEmail = async () => {
    if (selectedTeacherIds.length < 1) {
      setError("Choose at least one teacher to email.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const { groupedResults, classNames } = await fetchAndPackageResults();
      const emailPayload = buildAladdinEmailPayload({
        groupedResults,
        selectedClassIds,
        assessmentName: selectedAssessmentName,
        schoolName: prefill?.schoolName || prefill?.school_name || "School",
        rollNumber: prefill?.rollNumber || prefill?.rollnumber || "",
        academicYearKey: academicYear.key
      });
      const recipients = exportTeachers.filter((teacher) => selectedTeacherIds.includes(teacher.id));

      await postAladdinExportAuditLog(
        teacherid,
        schoolid,
        selectedAssessmentName,
        selectedScreenerId,
        selectedClassIds,
        classNames.length ? classNames : selectedClassNames,
        "Email"
      );

      const sendResults = [];
      for (const recipient of recipients) {
        try {
          await sendAladdinResultsEmail(recipient.email, getTeacherLabel(recipient), emailPayload);
          sendResults.push({ recipient, success: true });
        } catch (sendError) {
          console.error(`Failed to send Aladdin export email to ${recipient.email}:`, sendError);
          sendResults.push({ recipient, success: false });
        }
      }

      const successCount = sendResults.filter((result) => result.success).length;
      setSuccessDetails({
        type: "email",
        title: successCount === recipients.length ? "Email Sent" : successCount > 0 ? "Email Partially Sent" : "Email Failed",
        message:
          successCount === recipients.length
            ? `The export was emailed to ${successCount} teacher${successCount === 1 ? "" : "s"}.`
            : successCount > 0
              ? `The export was emailed to ${successCount} of ${recipients.length} teachers.`
              : "The export could not be emailed to the selected teachers."
      });
      setStage("success");
    } catch (emailError) {
      console.error("Error emailing Aladdin export:", emailError);
      setError(emailError?.message || "The export could not be emailed. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitNotificationRequest = async () => {
    if (selectedClassIds.length < 1) {
      setError("Choose at least one class before sending this request.");
      return;
    }

    setIsSubmitting(true);
    setError("");

    const classIdsToNotify = readinessResults.length
      ? readinessResults.filter((result) => !result.ready).map((result) => result.classId)
      : selectedClassIds;

    try {
      let firstResponse = null;

      for (const classId of classIdsToNotify) {
        const target = { type: "class", classId };

        if (hasPlaceholderTargetId(target)) {
          setError("Choose real classes before sending this request.");
          return;
        }

        const response = await requestAladdinExportNotify(schoolid, classId, {
          assessmentId: selectedScreenerId,
          assessmentName: selectedAssessmentName,
          note: ["Tell me when export is ready", teacherEmail].filter(Boolean).join(" | ")
        });
        assertSubmittedRequestResponse(response);
        if (!firstResponse) firstResponse = response;
      }

      setSupportReference(getSupportRequestReference(firstResponse));
      setIsDuplicatePending(isDuplicatePendingResponse(firstResponse));
      setSuccessDetails({
        type: "notify",
        title: "Notification Request Sent",
        message: `We will tell you when the ${selectedAssessmentName} export is ready.`
      });
      setStage("success");
    } catch (submitError) {
      setError(getSupportRequestErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = (message = successDetails?.message || "Assessment export workflow complete.") => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference,
        message
      });
      return;
    }

    handleClose();
  };

  const resetForAnotherExport = () => {
    setStage("screeners");
    setSelectedScreenerId("");
    setClasses([]);
    setSelectedClassIds([]);
    setSelectedTeacherIds([]);
    setReadinessResults([]);
    setSupportReference("");
    setIsDuplicatePending(false);
    setSuccessDetails(null);
    setError("");
  };

  const renderLockedYear = () => (
    <Alert variant="secondary" className="teacher-round-three-warning">
      <strong>Academic year locked</strong>
      <span>
        This flow exports the current academic year: {academicYear.key} ({academicYear.start} to {academicYear.end}).
      </span>
    </Alert>
  );

  const renderGuidance = () => (
    <div className="teacher-round-three-section">
      <Button
        variant="outline-secondary"
        onClick={() => setShowGuidance((current) => !current)}
        aria-controls="aladdin-export-guidance"
        aria-expanded={showGuidance}
      >
        {showGuidance ? "Hide Aladdin guidance" : "Show Aladdin guidance"}
      </Button>
      <Collapse in={showGuidance}>
        <div id="aladdin-export-guidance" className="teacher-round-three-next mt-3">
          <strong>How to import ALPACA results into Aladdin</strong>
          <div className="ratio ratio-16x9 mt-2 mb-3">
            <iframe
              src={ALADDIN_IMPORT_VIDEO_EMBED_URL}
              title="ALPACA to Aladdin import tutorial"
              allowFullScreen
            />
          </div>
          <a className="me-2 btn btn-outline-primary" href={ALADDIN_IMPORT_VIDEO_URL} target="_blank" rel="noreferrer">
            Open video in a new tab
          </a>
          <a className="btn btn-outline-primary" href={ALADDIN_IMPORT_PDF_URL} target="_blank" rel="noreferrer">
            Download PDF guide
          </a>
          <span>The PDF guide is also included in each downloaded ZIP.</span>
        </div>
      </Collapse>
    </div>
  );

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" className="teacher-round-three-modal" size="lg">
      <Modal.Header closeButton>
        <Modal.Title as="h2">Aladdin Export</Modal.Title>
      </Modal.Header>

      {stage === "screeners" && (
        <>
          <Modal.Body>
            <p className="teacher-round-three-intro">Choose the screener you want to export.</p>
            {renderLockedYear()}
            {error && <Alert variant="danger">{error}</Alert>}
            {isLoadingScreeners ? (
              <div className="teacher-round-three-search-state">
                <Spinner animation="border" size="sm" />
                <span>Loading screeners...</span>
              </div>
            ) : screeners.length < 1 ? (
              <Alert variant="warning" className="teacher-round-three-warning">
                <strong>No Aladdin screeners available</strong>
                <span>Screeners must have the standardised scores tag switched on before teachers can export them.</span>
              </Alert>
            ) : (
              <div className="teacher-round-three-options">
                {screeners.map((screener) => (
                  <label
                    key={getAssessmentId(screener)}
                    className={`teacher-round-three-option${selectedScreenerId === getAssessmentId(screener) ? " selected" : ""}`}
                  >
                    <Form.Check
                      type="radio"
                      name="aladdinScreener"
                      checked={selectedScreenerId === getAssessmentId(screener)}
                      onChange={() => {
                        setSelectedScreenerId(getAssessmentId(screener));
                        setStage("classes");
                      }}
                      aria-label={getAssessmentName(screener)}
                    />
                    <span>
                      <strong>{getAssessmentName(screener)}</strong>
                      <span>Enabled for Aladdin export</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
            <Button variant="outline-primary" onClick={loadTagEnabledScreeners} disabled={isLoadingScreeners}>
              Retry
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "classes" && (
        <>
          <Modal.Body>
            <p className="teacher-round-three-intro">Select the class results to include in the export.</p>
            {renderLockedYear()}
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="teacher-round-three-next">
              <strong>{selectedAssessmentName}</strong>
              <span>{selectedClassIds.length} of {classes.length} classes selected.</span>
            </div>
            {isLoadingClasses ? (
              <div className="teacher-round-three-search-state">
                <Spinner animation="border" size="sm" />
                <span>Loading classes...</span>
              </div>
            ) : classes.length < 1 ? (
              <Alert variant="info" className="teacher-round-three-warning">
                <strong>No classes found</strong>
                <span>No classes have exportable results for this screener in {academicYear.key}.</span>
              </Alert>
            ) : (
              <div className="table-responsive teacher-round-three-section">
                <Table bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>Children</th>
                      <th>
                        <Form.Check
                          type="checkbox"
                          label="Select all"
                          checked={classes.length > 0 && selectedClassIds.length === classes.length}
                          onChange={handleSelectAllClasses}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {classes.map((classItem) => {
                      const classId = getClassId(classItem);
                      const className = getClassName(classItem);
                      const classDetail = [
                        getDistinctClassGradeLabel(classItem, className, countryGrades),
                        getClassAcademicYear(classItem)
                      ].filter(Boolean).join(" - ");

                      return (
                        <tr key={classId}>
                          <td>
                            <strong>{className}</strong>
                            {classItem.trial_class && <span className="ms-2 badge bg-warning text-dark">FAKE</span>}
                            {classDetail && <div className="text-muted small">{classDetail}</div>}
                          </td>
                          <td>{classItem.studentNumber || classItem.studentCount || 0}</td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={selectedClassIds.includes(classId)}
                              onChange={() => toggleClassSelection(classId)}
                              aria-label={`Select ${formatClassDisplayName(classItem, className, countryGrades)}`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
            {renderGuidance()}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setStage("screeners")}>Back</Button>
            <Button variant="primary" onClick={checkReadiness} disabled={selectedClassIds.length < 1 || isSubmitting || isLoadingClasses}>
              {isSubmitting ? "Checking..." : "Check readiness"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "ready" && (
        <>
          <Modal.Body>
            <p className="teacher-round-three-intro">The selected exports are ready.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="success" className="teacher-round-three-warning">
              <strong>{selectedAssessmentName} is ready to export.</strong>
              <span>{selectedClassIds.length} class{selectedClassIds.length === 1 ? "" : "es"} selected for {academicYear.key}.</span>
            </Alert>
            <div className="teacher-round-three-next">
              <strong>Selected classes</strong>
              <span>{selectedClassLabels.join(", ")}</span>
            </div>
            {renderGuidance()}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setStage("classes")}>Back</Button>
            <Button variant="outline-primary" onClick={openEmailStep} disabled={isSubmitting}>
              Email export
            </Button>
            <Button variant="primary" onClick={handleDownloadExport} disabled={isSubmitting}>
              {isSubmitting ? "Preparing..." : "Download export"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "not-ready" && (
        <>
          <Modal.Body>
            <p className="teacher-round-three-intro">Some selected exports are not ready yet.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="teacher-round-three-warning">
              <strong>Export readiness needs review</strong>
              <span>No file was downloaded. You can request a notification when the export is ready.</span>
            </Alert>
            <div className="teacher-round-three-results">
              {readinessResults.map((result) => {
                const classItem = classes.find((item) => getClassId(item) === result.classId);

                return (
                  <div key={result.classId} className={`teacher-round-three-row${result.ready ? " selected" : ""}`}>
                    <span>
                      <strong>{classItem ? formatClassDisplayName(classItem, "Class unavailable", countryGrades) : result.classId}</strong>
                      <span>{result.ready ? "Ready" : "Not ready"}</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setStage("classes")}>Back</Button>
            <Button variant="primary" onClick={handleSubmitNotificationRequest} disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Tell me when ready"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "email" && (
        <>
          <Modal.Body>
            <p className="teacher-round-three-intro">Choose who should receive the Aladdin export.</p>
            {error && <Alert variant="danger">{error}</Alert>}
            {isLoadingTeachers ? (
              <div className="teacher-round-three-search-state">
                <Spinner animation="border" size="sm" />
                <span>Loading teachers...</span>
              </div>
            ) : exportTeachers.length < 1 ? (
              <Alert variant="info" className="teacher-round-three-warning">
                <strong>No eligible recipients found</strong>
                <span>Recipients need SENCO, Principal, Secretary, or Alpaca Lead roles.</span>
              </Alert>
            ) : (
              <div className="teacher-round-three-options">
                {exportTeachers.map((teacher) => (
                  <label key={teacher.id} className={`teacher-round-three-option${selectedTeacherIds.includes(teacher.id) ? " selected" : ""}`}>
                    <Form.Check
                      type="checkbox"
                      checked={selectedTeacherIds.includes(teacher.id)}
                      onChange={() =>
                        setSelectedTeacherIds((currentIds) =>
                          currentIds.includes(teacher.id)
                            ? currentIds.filter((teacherId) => teacherId !== teacher.id)
                            : [...currentIds, teacher.id]
                        )
                      }
                      aria-label={`Email ${getTeacherLabel(teacher)}`}
                    />
                    <span>
                      <strong>{getTeacherLabel(teacher)}</strong>
                      <span>{teacher.email}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setStage("ready")}>Back</Button>
            <Button
              variant="primary"
              onClick={handleSendEmail}
              disabled={isSubmitting || selectedTeacherIds.length < 1 || isLoadingTeachers}
            >
              {isSubmitting ? "Sending..." : "Send email"}
            </Button>
          </Modal.Footer>
        </>
      )}

      {stage === "success" && (
        <>
          <Modal.Body>
            <p className="teacher-round-three-intro">{successDetails?.message || "The Aladdin export workflow is complete."}</p>
            <Alert variant={successDetails?.type === "email" && successDetails?.title === "Email Failed" ? "warning" : "success"} className="teacher-round-three-success">
              <span className="teacher-round-three-ok" aria-hidden="true">OK</span>
              <span>
                <strong>{successDetails?.title || "Done"}</strong>
                <span>
                  {isDuplicatePending
                    ? "The Aladdin export notification request is already in progress."
                    : successDetails?.type === "notify"
                      ? "No export was downloaded automatically."
                      : "The workflow stayed inside this flow."}
                </span>
              </span>
            </Alert>
            <div className="teacher-round-three-next">
              <strong>What next?</strong>
              <span>{embedded ? "Done returns to SENSAI" : "Done closes this flow"}. Check another export repeats the action.</span>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={resetForAnotherExport}>Check another export</Button>
            <Button variant="primary" onClick={() => handleDone()}>Done</Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
}

export default TeacherAladdinExportModal;
